/** Refraction as an autorefractometer prints it (SPH / CYL / AXIS) and its clinical description (D-019). */

export interface Refraction {
  sph: number;
  cyl: number;
  axis: number;
}

/** Axis in 1..180, the autorefractometer convention (0 is printed as 180). */
export function normalizeAxis(a: number): number {
  let x = Math.round(a) % 180;
  if (x <= 0) x += 180;
  return x;
}

/** Plus-cylinder → minus-cylinder form (standard transposition). Minus-cylinder input only normalises the axis. */
export function toMinusCylinder(r: Refraction): Refraction {
  if (r.cyl > 0) return { sph: r.sph + r.cyl, cyl: -r.cyl, axis: normalizeAxis(r.axis + 90) };
  return { sph: r.sph, cyl: r.cyl === 0 ? 0 : r.cyl, axis: normalizeAxis(r.axis) };
}

export function sphericalEquivalent(sph: number, cyl: number): number {
  return sph + cyl / 2;
}

export type AstigmatismType =
  | 'simple_myopic' | 'compound_myopic' | 'simple_hyperopic' | 'compound_hyperopic' | 'mixed';

export type RefractionKind = 'emmetropia' | 'myopia' | 'hyperopia' | AstigmatismType;

export interface RefractionClass {
  kind: RefractionKind;
  /** Spherical equivalent, D. */
  se: number;
  /** Powers of the two principal meridians, D. */
  m1: number;
  m2: number;
}

const EPS = 1e-6;

/** Clinical description of a refraction in minus-cylinder form (C ≤ 0). */
export function describeRefraction(S: number, C: number): RefractionClass {
  const m1 = S;
  const m2 = S + C;
  const se = S + C / 2;
  if (C > -EPS) {
    const kind: RefractionKind = Math.abs(S) < EPS ? 'emmetropia' : S < 0 ? 'myopia' : 'hyperopia';
    return { kind, se, m1, m2 };
  }
  let kind: AstigmatismType;
  if (Math.abs(m1) < EPS) kind = 'simple_myopic';
  else if (m1 < 0) kind = 'compound_myopic';
  else if (Math.abs(m2) < EPS) kind = 'simple_hyperopic';
  else if (m2 > 0) kind = 'compound_hyperopic';
  else kind = 'mixed';
  return { kind, se, m1, m2 };
}
